import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import SkeletonBlock from '../components/SkeletonBlock';
import { announcementEntriesAPI } from '../services/api';
import {
  trackAnnouncementPageViewed,
  trackAnnouncementHelpCenterClicked,
} from '../services/analytics';

const TYPE_PILL = {
  new: { label: 'NEW', cls: 'bg-[#F3FAF7] text-[#06A561]' },
  improved: { label: 'IMPROVED', cls: 'bg-[#FFF8F2] text-[#FF7300]' },
  bugfix: { label: 'BUG FIX', cls: 'bg-[#FEF6FA] text-[#EC4899]' },
  announcement: { label: 'ANNOUNCEMENT', cls: 'bg-[#FFFCF4] text-[#9D8233]' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    .toUpperCase();
}

export default function Announcement() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    announcementEntriesAPI
      .list()
      .then((res) => {
        if (!alive || !res.success) return;
        const list = res.entries || [];
        setEntries(list);
        const latest = list[0];
        trackAnnouncementPageViewed({
          entriesCount: list.length,
          latestEntryDate: latest?.date,
          latestEntryType: latest?.type,
        });
      })
      .catch(() => { })
      .finally(() => {
        if (alive) setLoading(false);
      });

    // Mark as read → clears the yellow dot. Notify DashNav so it updates
    // without a reload.
    announcementEntriesAPI
      .markSeen()
      .then(() => window.dispatchEvent(new Event('whatsnew:seen')))
      .catch(() => { });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [loading, entries]);

  return (
    <div className="min-h-screen bg-[#F5F4FF]">

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 py-10 sm:py-14">
        {/* Header */}
        <div
          className="max-w-[800px] mx-auto text-center mb-[25px] sm:mb-[40px]"
          data-aos="fade-up"
          data-aos-duration="500"
          data-aos-delay="0"
        >
          <h1 className="font-inter font-regular text-[28px] sm:text-[40px] text-[#0F172A]">
            Announcements
          </h1>
          <p className="max-w-[550px] mx-auto font-inter text-[14px] sm:text-[16px] text-[#0F172A] mt-[10px] leading-relaxed">
            Important Reviewly updates, feature improvements, and announcements for  your Civil Service Exam preparation.
          </p>
        </div>

        {/* Entries */}
        <div className="max-w-[800px] mx-auto space-y-[24px]">
          {loading ? (
            <>
              {[...Array(3)].map((_, idx) => (
                <article
                  key={idx}
                  className="bg-white rounded-[12px] border border-[#EFF0F6] shadow-[0px_1px_8px_0px_#14142B0A] pt-[24px] px-[32px] pb-[32px] animate-pulse"
                  data-aos="fade-up"
                  data-aos-duration="500"
                  data-aos-delay={100 + idx * 50}
                >
                  <div className="flex items-center gap-[8px] flex-wrap mb-[4px]">
                    <SkeletonBlock className="h-[18px] w-24" />
                    <SkeletonBlock className="h-[24px] w-20 rounded-[8px]" />
                    <SkeletonBlock className="h-[24px] w-36 rounded-[8px]" />
                  </div>
                  <SkeletonBlock className="h-[24px] w-2/3 mb-[8px]" />
                  <SkeletonBlock className="h-[16px] w-full mb-[6px]" />
                  <SkeletonBlock className="h-[16px] w-full mb-[6px]" />
                  <SkeletonBlock className="h-[16px] w-[80%]" />
                  <div className="mt-[24px] rounded-[12px] bg-[#F7F6FC] overflow-hidden">
                    <SkeletonBlock className="h-[200px] w-full rounded-[12px]" />
                  </div>
                </article>
              ))}
            </>
          ) : entries.length === 0 ? (
            <div
              className="bg-white rounded-[16px] border border-[#EDEAF6] px-6 py-12 text-center"
              data-aos="fade-up"
              data-aos-duration="500"
              data-aos-delay="100"
            >
              <p className="font-inter text-[15px] text-[#6C737F]">
                No updates yet — check back soon. 💜
              </p>
            </div>
          ) : (
            entries.map((e, idx) => {
              const pill = TYPE_PILL[e.type] || TYPE_PILL.new;
              return (
                <article
                  key={e._id}
                  className="bg-white rounded-[12px] border border-[#EFF0F6] shadow-[0px_1px_8px_0px_#14142B0A] pt-[24px] px-[32px] pb-[32px]"
                  data-aos="fade-up"
                  data-aos-duration="500"
                  data-aos-delay={100 + idx * 50}
                >
                  {/* Date + pills row */}
                  <div className="flex items-center gap-[8px] flex-wrap mb-[4px]">
                    <span className="font-inter text-[14px] font-regular tracking-wide text-[#6C737F]">
                      {formatDate(e.date)}
                    </span>
                    <span
                      className={`font-inter font-regular text-[13px] leading-none px-[12px] py-[4px] rounded-[8px] ${pill.cls}`}
                    >
                      {pill.label}
                    </span>
                    {e.feedbackTag && (
                      <span className="font-inter font-regular text-[13px] leading-none px-[12px] py-[4px] rounded-[8px] bg-[#F6F6F7] text-[#6C737F]">
                        BASED ON USER FEEDBACK
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h2 className="font-inter font-medium text-[16px] sm:text-[18px] text-[#0F172A] mb-[4px]">
                    {e.title}
                  </h2>

                  {/* Description */}
                  <p className="font-inter text-[14px] text-[#0F172A] leading-relaxed whitespace-pre-line">
                    {e.description}
                  </p>

                  {/* Image */}
                  {e.imageUrl && (
                    <div className="mt-[24px] bg-[#F7F6FC] max-h-[320px] overflow-hidden">
                      <img
                        src={e.imageUrl}
                        alt={e.title}
                        className="w-full"
                        loading="lazy"
                      />
                    </div>
                  )}
                </article>
              );
            })
          )}
        </div>

        {/* Footer */}
        <p
          className="font-inter text-[16px] text-[#0F172A] text-center mt-[40px]"
          data-aos="fade-up"
          data-aos-duration="500"
          data-aos-delay="150"
        >
          Have suggestions or spot an issue? Let us know through the{' '}
          <Link
            to="/dashboard/help"
            onClick={trackAnnouncementHelpCenterClicked}
            className="text-[#6E43B9] underline underline-offset-2 hover:text-[#5C36A0]"
          >
            Help Center
          </Link>
          .
        </p>
      </main>
    </div>
  );
}
